import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, ExternalAPI, FileRequestAction, RequestMethod, RobotCloudAPI } from '@moderepo/mode-apis';
import { capitalizeAll, capitalizeFirst, getErrorMessage, MemorySize, SelectInputOption, Time } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { clearFileRequests, createDownloadFileFromControlServerRequest, createDownloadFileFromDeviceRequest, createDownloadFileFromRobotRequest,
    deleteFileRequest, rcDataContext, updateUploadFileToControlServerRequestMetadata, updateUploadFileToDeviceRequestMetadata,
    updateUploadFileToRobotsRequestMetadata } from '@moderepo/mode-data-state-robot-cloud';
import { modeUIContext, setIsEditingForm, setIsLoading, showErrorNotification, showSuccessNotification } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import moment from 'moment';
import produce, { Draft } from 'immer';
import { Agent, Project } from '@moderepo/mode-internal-apis';
import { RCCreateFileRequest, RCCreateFileRequestInputs, RCCreateFileRequestProps } from '../../../components/robot-cloud';
import { useRitzTranslation } from '../../../utils/customHooks';
import { getGatewayFileTransferConfig, getSupportedFileType, MAX_FILE_REQUEST_UPLOAD_FILE_SIZE, RCGatewayConfig, useGetRCGatewayConfig }
    from '../RCUtils';
import { FileRequestInputErrors, validateCreateDownloadFileRequestInputs, validateCreateUploadFileRequestInputs } from './fileTransferLogic';
import { getMemorySizeDisplayValue } from '../../containerUtils';



/**
 * Create an Array of File Type options to be used for the Select Filetype input field.
 * @param gatewayConfig
 * @param trans
 * @param fileRequestActionType
 * @param project
 * @param loggedInAgent
 * @returns
 */
const createFileTypeOptions = (
    gatewayConfig: RCGatewayConfig | undefined,
    trans: TFunction,
    fileRequestActionType: FileRequestAction,
    project: Project | undefined,
    loggedInAgent: Agent | undefined,
): readonly SelectInputOption<string>[] => {
    const supportedFileTypes = getSupportedFileType(gatewayConfig, fileRequestActionType, project, loggedInAgent);
    return supportedFileTypes.map((fileType) => {
        return {
            label: fileType.name,
            value: fileType.value,
        };
    });
};


/**
 * The DEFAULT download request date range to use when the user wants to create a new request. The range will be 1 day.
 * The user can still choose a different date in the UI
 */
const downloadRequestDefaultDateRange = {
    startDate: Date.now() - Time.DAY_IN_MS,
    endDate  : Date.now(),
};



export interface RCCreateFileRequestContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly homeId: number;
    readonly deviceId: number;
    readonly robotId?: string | undefined;
    readonly fileRequestActionType: FileRequestAction;
    readonly onCancel: ()=> void;
    readonly onFileRequestCreated: ()=> void;
}


export const RCCreateFileRequestContainer: React.FC<RCCreateFileRequestContainerProps> = (props: RCCreateFileRequestContainerProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { projectId } = props;
    const project = selectProjectById(dataState, projectId);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const { trans } = useRitzTranslation(project);
    const isDownloadRequest = props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_DEVICE
        || props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER
        || props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_ROBOT;

    const isUploadRequest = props.fileRequestActionType === FileRequestAction.UPLOAD_TO_DEVICE
        || props.fileRequestActionType === FileRequestAction.UPLOAD_TO_CONTROL_SERVER
        || props.fileRequestActionType === FileRequestAction.UPLOAD_TO_ROBOT;

    const [inputErrors, setInputErrors] = useState<FileRequestInputErrors>({});


    // Get the Gateway config for the selectedGateway. We will need to get FileTransfer settings from this config.
    const selectedGatewayConfig = useGetRCGatewayConfig(props.deviceId);


    // Create an Array of File Type of options to be used for the FileType input field.
    const fileTypeOptions = useMemo(() => {
        return createFileTypeOptions(selectedGatewayConfig, trans, props.fileRequestActionType, project, loggedInAgent);
    }, [loggedInAgent, project, props.fileRequestActionType, selectedGatewayConfig, trans]);



    // Create a min/max Date range the user is allowed to select. We will use the project's creation date as the MIN date and NOW as the MAX date
    const downloadRequestDateRange = useMemo(() => {
        if (project) {
            return {
                minDate: moment(project.creationTime).valueOf(),
                maxDate: Date.now(),
            };
        }

        // If project is not fetched, use
        return {
            minDate: Date.now() - Time.MONTH_IN_MS,
            maxDate: Date.now(),
        };
    }, [project]);



    /**
     * This Container is used for creating a new Request therefore we will automatically enable EDIT mode as soon as the component load
     */
    useEffect(() => {
        uiDispatch(setIsEditingForm(true));
    }, [uiDispatch]);



    /**
     * Close the Edit mode
     */
    const onCloseEditMode = useCallback(() => {
        uiDispatch(setIsEditingForm(false));
    }, [uiDispatch]);



    const onCreateRequestFormSubmit = useCallback(async (inputs: RCCreateFileRequestInputs) => {
        const fileTransferSettings = getGatewayFileTransferConfig(selectedGatewayConfig, project, loggedInAgent);

        // If fileTransferSettings.c2dMaximumFileSize is set, use it BUT multiply it by MemorySize.MEGABYTE because the
        // c2dMaximumFileSize value is in MB and we want BYTES
        const maxUploadToDeviceFileSize = fileTransferSettings?.c2dMaximumFileSize
            ? fileTransferSettings?.c2dMaximumFileSize * MemorySize.MEGABYTE
            : MAX_FILE_REQUEST_UPLOAD_FILE_SIZE;

        const newErrors = isDownloadRequest
            ? validateCreateDownloadFileRequestInputs(inputs)
            : validateCreateUploadFileRequestInputs(inputs, maxUploadToDeviceFileSize);

        setInputErrors(newErrors);

        // Go through all the values in the newErrors object to find a non undefined value. If there is at least 1 non undefined value,
        // that mean there is an error.
        const hasErrors = Object.values(newErrors).some((error) => {
            return error !== undefined;
        });

        if (!hasErrors) {

            if (isDownloadRequest && inputs.fileType && inputs.startDate !== undefined && inputs.endDate !== undefined) {
                // Make API call to create File DOWNLOAD request
                try {

                    if (props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_DEVICE) {
                        await rcDataDispatch(createDownloadFileFromDeviceRequest(
                            props.homeId, props.deviceId, inputs.fileType,
                            (new Date(inputs.startDate)).toISOString(), (new Date(inputs.endDate)).toISOString(),
                            Boolean(fileTransferSettings?.useCustomDriver),
                        ));
                    } else if (props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER) {
                        await rcDataDispatch(createDownloadFileFromControlServerRequest(
                            props.homeId, props.deviceId, inputs.fileType,
                            (new Date(inputs.startDate)).toISOString(), (new Date(inputs.endDate)).toISOString(),
                            Boolean(fileTransferSettings?.useCustomDriver),
                        ));
                    } else if (props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_ROBOT && props.robotId) {
                        await rcDataDispatch(createDownloadFileFromRobotRequest(
                            props.homeId, props.deviceId, props.robotId, inputs.fileType,
                            (new Date(inputs.startDate)).toISOString(), (new Date(inputs.endDate)).toISOString(),
                            Boolean(fileTransferSettings?.useCustomDriver),
                        ));
                    }

                    // Everything is completed SUCCESSFULLY so now we can close the UI and notify the container
                    uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', {
                        term: capitalizeAll(trans('label.file_request')),
                    }));
                    onCloseEditMode();
                    props.onFileRequestCreated();

                } catch (error) {
                    // Ignore error because rcDataDispatch already handled showing the error message
                }
            } else if (isUploadRequest && inputs.uploadFile && inputs.fileType) {
                // Make API calls to create File UPLOAD request
                const file = inputs.uploadFile;
                uiDispatch(setIsLoading(true));

                try {
                    // the response will be the S3 URL where we can upload the file to. We will build a FormData with all the params
                    // from the URL and some params from the file object and then make an API call to this URL
                    const updateLoadFile = inputs.uploadFile;
                    const uploadResponse = await (async () => {
                        if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_DEVICE) {
                            return await RobotCloudAPI.getInstance().createUploadFileToDeviceRequest(
                                props.homeId, props.deviceId, updateLoadFile.name, Boolean(fileTransferSettings?.useCustomDriver),
                            );
                        }
                        if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_CONTROL_SERVER) {
                            return await RobotCloudAPI.getInstance().createUploadFileToControlServerRequest(
                                props.homeId, props.deviceId, updateLoadFile.name, Boolean(fileTransferSettings?.useCustomDriver),
                            );
                        }
                        // UPLOAD_TO_ROBOT
                        if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_ROBOT && props.robotId) {
                            return await RobotCloudAPI.getInstance().createUploadFileToRobotsRequest(
                                props.homeId, props.deviceId, updateLoadFile.name, Boolean(fileTransferSettings?.useCustomDriver),
                            );
                        }
                        return false;
                    })();

                    if (!uploadResponse) {
                        return;
                    }

                    // Create a FormData object and put all the params from the uploadURL into this object and also the file
                    const formData = new FormData();

                    const url = new URL(uploadResponse.uploadUrl);
                    const params = url.searchParams;
                    params.forEach((value, key) => {
                        formData.append(key, value);
                    });

                    formData.append('file', file);

                    // Now we will make an API call to S3 to upload the file and then make an API call to our backend to update the request's
                    // metadata. However, these API call might fail and when one of them failed, we will need to DELETE the request because
                    // if one of these calls failed then the request is kind of useless. It would be weird to keep them and make the user delete
                    // them manually
                    try {
                        // Make API call to upload to S3

                        const response = await ExternalAPI.getInstance().sendFormRequest(RequestMethod.PUT, uploadResponse.uploadUrl, formData);
                        const checksum = response.headers.etag ? JSON.parse(response.headers.etag) : '';


                        // Make API call to update request's metadata
                        if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_DEVICE) {
                            await rcDataDispatch(updateUploadFileToDeviceRequestMetadata(props.homeId, uploadResponse.id, {
                                fileSetSize: file.size, fileSetChecksum: checksum, fileType: inputs.fileType,
                            }));
                        } else if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_CONTROL_SERVER) {
                            await rcDataDispatch(updateUploadFileToControlServerRequestMetadata(props.homeId, uploadResponse.id, {
                                fileSetSize: file.size, fileSetChecksum: checksum, fileType: inputs.fileType,
                            }));
                        } else if (props.robotId) {
                            await rcDataDispatch(updateUploadFileToRobotsRequestMetadata(props.homeId, uploadResponse.id, props.robotId, {
                                fileSetSize: file.size, fileSetChecksum: checksum, fileType: inputs.fileType,
                            }));
                        }

                        // Everything is completed SUCCESSFULLY so now we can close the UI and notify the container
                        uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', {
                            term: capitalizeAll(trans('label.file_request')),
                        }));
                        onCloseEditMode();
                        props.onFileRequestCreated();

                        // Since we added a new file request, the current list of file requests in the UI is not up-to-date anymore.
                        // We need to clear all the file requests so that the UI will reload the list of file requests
                        rcDataDispatch(clearFileRequests(props.homeId));

                    } catch (error) {
                        uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));

                        // Unable to upload to S3 or unable to update request's metadata so we need to delete the request and have the user try again
                        // We will not close the create request UI yet so that the user can try again
                        rcDataDispatch(deleteFileRequest(props.homeId, uploadResponse.id));
                    }
                } catch (error) {
                    // If we get here then it must be because of the call 'createUploadFileToDeviceRequest'. Show an error message and have the
                    // user try again
                    uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                }

                uiDispatch(setIsLoading(false));
            }
        }

    }, [isDownloadRequest, isUploadRequest, loggedInAgent, onCloseEditMode, project, props, rcDataDispatch, selectedGatewayConfig, trans,
        uiDispatch]);



    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onCreateRequestFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: Draft<FileRequestInputErrors>) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);



    /**
     * Create the input fields for the RCCreateFileRequest comp and cache them
     */
    const createFileRequestCompFields = useMemo((): RCCreateFileRequestProps['fields'] => {

        const fileTransferSettings = getGatewayFileTransferConfig(selectedGatewayConfig, project, loggedInAgent);

        // If fileTransferSettings.c2dMaximumFileSize is set, use it BUT multiply it by MemorySize.MEGABYTE because the
        // c2dMaximumFileSize value is in MB and we want BYTES
        const maxUploadToDeviceFileSize = fileTransferSettings?.c2dMaximumFileSize
            ? fileTransferSettings?.c2dMaximumFileSize * MemorySize.MEGABYTE
            : MAX_FILE_REQUEST_UPLOAD_FILE_SIZE;

        // For Create DOWNLOAD request, show these input fields
        if (isDownloadRequest) {
            return {
                fileType: {
                    label   : capitalizeAll(trans('file_transfer.input.label.file_type')),
                    required: true,
                    editable: true,
                    options : fileTypeOptions,

                    // use the FIRST option as default if there are more than 1 options
                    value: fileTypeOptions.length > 0 ? fileTypeOptions[0].value : undefined,

                    // Hide this input if there is EXACTLY 1 option because we already selected by default. If there are 0 options, we will show
                    // this input, NOT hide it because this input is required so if there is no option for the user to choose, we want to show
                    // some error so that the user know why they can't create a request
                    hidden: fileTypeOptions.length === 1,
                    error : inputErrors.fileType ? capitalizeFirst(trans(inputErrors.fileType)) : undefined,
                },
                startDate: {
                    label   : capitalizeAll(trans('label.start_date')),
                    required: true,
                    editable: true,
                    value   : downloadRequestDefaultDateRange.startDate,
                    error   : inputErrors.startDate ? capitalizeFirst(trans(inputErrors.startDate)) : undefined,
                },
                endDate: {
                    label   : capitalizeAll(trans('label.end_date')),
                    required: true,
                    editable: true,
                    value   : downloadRequestDefaultDateRange.endDate,
                    error   : inputErrors.endDate ? capitalizeFirst(trans(inputErrors.endDate)) : undefined,
                },
            };
        }

        // For Create UPLOAD request, there is only 1 input field, 'uploadFile' field
        if (isUploadRequest) {
            return {
                fileType: {
                    label   : capitalizeAll(trans('file_transfer.input.label.file_type')),
                    required: true,
                    editable: true,
                    options : fileTypeOptions,

                    // use the FIRST option as default if there are more than 1 options
                    value: fileTypeOptions.length > 0 ? fileTypeOptions[0].value : undefined,

                    // Hide this input if there is EXACTLY 1 option because we already selected by default. If there are 0 options, we will show
                    // this input, NOT hide it because this input is required so if there is no option for the user to choose, we want to show
                    // some error so that the user know why they can't create a request
                    hidden: fileTypeOptions.length === 1,
                    error : inputErrors.fileType ? capitalizeFirst(trans(inputErrors.fileType)) : undefined,
                },
                uploadFile: {
                    label      : capitalizeAll(trans('input.file.label')),
                    description: capitalizeAll(trans('file_transfer.input.file.hint', {
                        file_size: getMemorySizeDisplayValue(maxUploadToDeviceFileSize, trans),
                    })),
                    required: true,
                    editable: true,
                    value   : undefined,
                    error   : inputErrors.uploadFile ? capitalizeFirst(trans(inputErrors.uploadFile)) : undefined,
                },
            };
        }

        return {};
    }, [fileTypeOptions, inputErrors.endDate, inputErrors.fileType, inputErrors.startDate, inputErrors.uploadFile, isDownloadRequest, isUploadRequest,
        loggedInAgent, project, selectedGatewayConfig, trans]);



    /**
     * Crate the 'texts' required for the RCCreateFileRequest comp and cache them
     */
    const createFileRequestCompTexts = useMemo((): RCCreateFileRequestProps['texts'] => {
        return {
            dateInputLabel: capitalizeFirst(trans('select the start/end date')),
            dateFormat    : 'YYYY/MM/DD HH:mm:ss',
            todayLabel    : capitalizeFirst(trans('label.today')),
            okLabel       : capitalizeFirst(trans('label.ok')),
            cancelLabel   : capitalizeFirst(trans('label.cancel')),
        };
    }, [trans]);



    /**
     * Create the actions required for the RCCreateFileRequest comp and cache them
     */
    const createFileRequestFormActions = useMemo(() => {
        return {
            cancelEdit: {
                label  : capitalizeAll(trans('label.cancel')),
                onClick: () => {
                    onCloseEditMode();
                    props.onCancel();
                },
            },
            saveUpdate: {
                label  : capitalizeAll(trans('label.create')),
                onClick: onCreateRequestFormSubmit,
            },
        };
    }, [onCloseEditMode, onCreateRequestFormSubmit, props, trans]);


    /**
     * The RCCreateFileRequest can be used for creating many different type of file request so we need to show different
     * title based on the file request action type
     */
    const panelTitleKey = useMemo(() => {
        if (props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_DEVICE) {
            return 'file_transfer.panel.title.create_download_from_device_request';
        }
        if (props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER) {
            return 'file_transfer.panel.title.create_download_from_control_server_request';
        }
        if (props.fileRequestActionType === FileRequestAction.DOWNLOAD_FROM_ROBOT) {
            return 'file_transfer.panel.title.create_download_from_robot_request';
        }
        if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_DEVICE) {
            return 'file_transfer.panel.title.create_upload_to_device_request';
        }
        if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_CONTROL_SERVER) {
            return 'file_transfer.panel.title.create_upload_to_control_server_request';
        }
        if (props.fileRequestActionType === FileRequestAction.UPLOAD_TO_ROBOT) {
            return 'file_transfer.panel.title.create_upload_to_robot_request';
        }
        return '';
    }, [props.fileRequestActionType]);



    return (
        <RCCreateFileRequest
            className={clsx(props.className)}
            isEditing
            title={capitalizeAll(trans(panelTitleKey))}
            icon={isDownloadRequest ? 'fas fa-file-download' : 'fas fa-file-upload'}
            fields={createFileRequestCompFields}
            minDate={downloadRequestDateRange.minDate}
            maxDate={downloadRequestDateRange.maxDate}
            texts={createFileRequestCompTexts}
            actions={createFileRequestFormActions}
            onFormInputChange={onCreateRequestFormInputChange}
        />
    );
};
